/**
 * Shopify Push Handler
 * 
 * This module handles pushing products to Shopify by creating new products
 * instead of updating existing ones. It processes products sequentially,
 * creates new products, and archives or deletes old ones.
 */

import { ShopifyProductCreateService } from '../services/shopifyProductCreateService';
import { hasEnoughCredits } from '../services/creditService';
import { deductCredits, refundCredits, refundCreditsForTemplateExecution, recordPushToShopify } from '../services/optimizedCreditService';

/**
 * Pushes products to Shopify by creating new products
 * @param {object} shop - Current shop
 * @param {object} user - Current user
 * @param {object} localEdits - Local edits to apply
 * @param {Array<object>} products - Products to process
 * @param {Function} setIsPushing - Function to set pushing state
 * @param {Function} setSavingProgress - Function to set saving progress
 * @param {Function} setLocalEdits - Function to set local edits
 * @param {Function} setHasUnsavedChanges - Function to set unsaved changes state
 * @param {Function} showToast - Function to show toast notifications
 * @param {Function} setError - Function to set error state
 * @param {Function} fetchProductsCallback - Function to fetch products
 * @param {object} options - Additional options
 * @returns {Promise<void>}
 */
export const pushProductsToShopify = async (
  shop,
  user,
  localEdits,
  products,
  setIsPushing,
  setSavingProgress,
  setLocalEdits,
  setHasUnsavedChanges,
  showToast,
  setError,
  fetchProductsCallback,
  options = {}
) => {
  if (!shop || !user) {
    showToast('Shop or user information missing', true);
    return;
  }

  setIsPushing(true);
  const totalProducts = Object.keys(localEdits).length;
  setSavingProgress({ current: 0, total: totalProducts, message: 'Preparing to create new products...' });

  // Extract any template execution metadata for potential refunds
  const templateExecutionMetadata = [];
  Object.entries(localEdits).forEach(([productId, productEdits]) => {
    // Check if this product has template execution metadata
    if (productEdits._templateExecutions) {
      templateExecutionMetadata.push(...productEdits._templateExecutions);
    }
  });
  
  console.log('Template execution metadata for potential refunds:', templateExecutionMetadata);

  try {
    // Check if user has enough credits first
    const hasCredits = await hasEnoughCredits(user.uid, totalProducts);
    
    if (!hasCredits) {
      showToast(`Not enough credits. You need ${totalProducts} credits to create these products.`, true);
      setIsPushing(false);
      return;
    }

    console.log('Credit check passed, proceeding with product creation...');
    
    // Log the localEdits to debug
    console.log('Local edits being passed to product creation:', localEdits);

    // Create service instance
    const productCreateService = new ShopifyProductCreateService(
      shop.shop,
      shop.accessToken
    );

    // Process products sequentially
    const results = await productCreateService.processProductsSequentially(
      products,
      localEdits,
      {
        deleteOldProducts: options.deleteOldProducts || false
      },
      (progress) => {
        setSavingProgress(progress);
      }
    );

    // Check if there were any errors
    if (results.errors && results.errors.length > 0) {
      console.error('Errors occurred during product creation:', results.errors);
      
      // Refund credits for failed products
      if (results.errors.length > 0) {
        try {
          // Refund credits for each failed product
          for (const error of results.errors) {
            if (error.productId) {
              await refundCredits(user.uid, 1, `Failed to create product: ${error.message || 'Unknown error'}`);
              console.log(`Refunded 1 credit for failed product ${error.productId}`);
            }
          }
          
          // Also refund for any template executions
          await refundCreditsForTemplateExecution(user.uid, templateExecutionMetadata);
        } catch (refundError) {
          console.error('Error refunding credits:', refundError);
        }
      }
      
      // Show error message with details
      const errorMessages = results.errors.map(err => err.message || 'Unknown error').join('; ');
      showToast(`Errors occurred during product creation: ${errorMessages}`, true, 10000);
      
      // If there were some successes, show a mixed message
      if (results.success.length > 0) {
        showToast(`Created ${results.success.length} products successfully, but ${results.errors.length} failed. Credits have been refunded for failed products.`, true, 10000);
      }
    } else {
      // Deduct credits for successful creations only if there were no errors
      if (results.success.length > 0) {
        // Ensure we have a valid product object with an id
        const productForCredit = results.success[0];
        const productData = {
          id: productForCredit?.id || `batch-creation-${Date.now()}`,
          title: productForCredit?.title || 'Batch Product Creation',
          updateType: 'bulk'
        };
        
        // Deduct credits for the successful products
        await deductCredits(user.uid, results.success.length);
        
        // Record each successful product push individually
        for (const successResult of results.success) {
          try {
            const productId = successResult.newProductId || successResult.id;
            await recordPushToShopify(user.uid, shop.id, productId);
            console.log(`Recorded push for product ${productId}`);
          } catch (pushError) {
            console.error(`Error recording push for product:`, pushError);
            // Continue even if recording fails for one product
          }
        }
      }

      // Update URL with new product IDs if there's only one product
      if (results.success.length === 1 && window.location.pathname.includes('/product/')) {
        const oldProductId = results.success[0].oldProductId;
        const newProductId = results.success[0].newProductId;
        
        // Replace old product ID with new one in URL
        const newUrl = window.location.href.replace(
          `/product/${oldProductId}`,
          `/product/${newProductId}`
        );
        
        // Use history.replaceState to update the URL without reloading
        window.history.replaceState({}, '', newUrl);
      }

      // Update URL with new product IDs if we're in bulk edit mode
      if (results.success.length > 0 && window.location.pathname.includes('/bulk-edit')) {
        // Get new product IDs
        const newProductIds = results.success.map(result => result.newProductId);
        
        // Create new URL with new product IDs
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('ids');
        newProductIds.forEach(id => searchParams.append('ids', id));
        
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        
        // Use history.replaceState to update the URL without reloading
        window.history.replaceState({}, '', newUrl);
      }

      // Show results if not reloading
      if (results.errors.length === 0) {
        showToast(`Successfully created ${results.success.length} new products${options.deleteOldProducts ? ' and deleted originals' : ' and archived originals'}. Check the logs for details.`, false, 10000);
        setLocalEdits({});
        setHasUnsavedChanges(false);
        
        // Store the success results in sessionStorage for later use
        sessionStorage.setItem('productCreationResults', JSON.stringify({
          success: results.success.length,
          productIds: results.success.map(result => result.newProductId),
          timestamp: Date.now(),
          deleteOldProducts: options.deleteOldProducts
        }));
        
        // Don't auto-refresh products to allow reading logs
        // The user can manually refresh the products later
        if (options.showConfirmModal && typeof options.setShowConfirmModal === 'function') {
          options.setShowConfirmModal(true);
        }
      }
    }
  } catch (error) {
    console.error('Error in push to Shopify:', error);
    showToast(`Error: ${error.message}`, true);
    
    // Refund credits for all products since the entire operation failed
    try {
      await refundCredits(user.uid, totalProducts, `Failed to push products: ${error.message || 'Unknown error'}`);
      console.log(`Refunded ${totalProducts} credits due to error: ${error.message}`);
      showToast(`Credits have been refunded due to error: ${error.message}`, true, 10000);
      
      // Also refund for any template executions
      await refundCreditsForTemplateExecution(user.uid, templateExecutionMetadata);
    } catch (refundError) {
      console.error('Error refunding credits:', refundError);
    }
  } finally {
    setIsPushing(false);
    setSavingProgress({ current: 0, total: 0, message: '' });
  }
};

/**
 * Handles the "Push to Shopify" button click
 * This is a wrapper function that can be used directly with the button's onAction prop
 */
export const handlePushToShopify = async (
  shop,
  user,
  localEdits,
  products,
  setIsPushing,
  setSavingProgress,
  setLocalEdits,
  setHasUnsavedChanges,
  showToast,
  setError,
  fetchProductsCallback,
  options = {}
) => {
  // Alias for pushProductsToShopify
  return pushProductsToShopify(
    shop,
    user,
    localEdits,
    products,
    setIsPushing,
    setSavingProgress,
    setLocalEdits,
    setHasUnsavedChanges,
    showToast,
    setError,
    fetchProductsCallback,
    options
  );
};
